"use client";

import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, Database, Download, Globe2, Play, Sparkles, UserRound, Waves } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Card, PageHeader } from "@/components/pulse-shell";
import { api, normalizePerception, type Perception } from "@/lib/api";

const HEAT_LABELS = ["Voice energy", "Speech rate", "Facial tension", "Gaze focus", "Posture shift", "Language sentiment"];
const STAGE_LABELS = ["Greeting", "Needs", "Explanation", "Decision", "Closing"];
const FUSION_LABELS: Array<[keyof NonNullable<Perception["fusion"]>, string]> = [
  ["ready", "Ready"],
  ["partial", "Partial"],
  ["pending", "Pending"],
  ["needs_review", "Needs review"],
];

const CALIB_KEY = "pulse.perception.calibration";

function fmtDelta(v: number | null | undefined, pct = false): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v > 0 ? "↑ +" : v < 0 ? "↓ " : "";
  const abs = Math.abs(v);
  return `${sign}${pct ? `${abs}%` : abs}`;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString();
}

/** Fixed 3-row grid: row 0/1/2 → y 20 / 80 / 140 (matches mock ribbons). */
function rowY(idx: number): number {
  return 20 + Math.min(2, Math.max(0, idx)) * 60;
}

function flowSvgPath(row: number): string {
  const y = rowY(row);
  // Slight mid-curve variety so parallel ribbons don't fully overlap
  const mid = y + (row === 1 ? -8 : row === 2 ? 8 : 0);
  return `M0 ${y} C70 ${y} 70 ${mid} 130 ${mid} S190 ${y} 260 ${y}`;
}

function curvePaths(curve: { x: number; y: number }[], w = 300, h = 70) {
  if (!curve.length) {
    return {
      line: "M0 65 C50 62 75 45 110 25 S165 4 205 24 S248 52 300 61",
      fill: "M0 65 C50 62 75 45 110 25 S165 4 205 24 S248 52 300 61 L300 70 L0 70Z",
    };
  }
  const pts = curve.map((p) => {
    const x = p.x * w;
    const y = h - 4 - p.y * (h - 10);
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const fill = `${line} L${last[0].toFixed(1)} ${h} L0 ${h}Z`;
  return { line, fill };
}

function curveYAt(curve: { x: number; y: number }[], xNorm: number, h = 70): number {
  if (!curve.length) return 20;
  let best = curve[0];
  let bestD = Infinity;
  for (const p of curve) {
    const d = Math.abs(p.x - xNorm);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return h - 4 - best.y * (h - 10);
}

function loadCalibPrefs() {
  try {
    const raw = localStorage.getItem(CALIB_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { threshold?: number; floor?: number; policy?: string; requireTwo?: boolean };
  } catch {
    return null;
  }
}

export function PerceptionPage() {
  const [chartMode, setChartMode] = useState("Valence × arousal");
  const [combined, setCombined] = useState(true);
  const [threshold, setThreshold] = useState(72);
  const [floor, setFloor] = useState(65);
  const [policy, setPolicy] = useState("Balanced");
  const [toast, setToast] = useState<{ precision: number; recall: number } | null>(null);
  const [requireTwo, setRequireTwo] = useState(true);
  const [range, setRange] = useState<"24h" | "7d" | "30d">("30d");
  const [agentId, setAgentId] = useState<string>("");
  const [languageFilter, setLanguageFilter] = useState("all");
  const [agents, setAgents] = useState<string[]>([]);
  const [data, setData] = useState<Perception>(() => normalizePerception({}));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prefs = loadCalibPrefs();
    if (!prefs) return;
    if (prefs.threshold != null) setThreshold(prefs.threshold);
    if (prefs.floor != null) setFloor(prefs.floor);
    if (prefs.policy) setPolicy(prefs.policy);
    if (prefs.requireTwo != null) setRequireTwo(prefs.requireTwo);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CALIB_KEY, JSON.stringify({ threshold, floor, policy, requireTwo }));
    } catch {
      /* ignore */
    }
  }, [threshold, floor, policy, requireTwo]);

  async function load(nextRange: typeof range, nextAgent: string) {
    setLoading(true);
    try {
      const live = await api.perception(nextRange, nextAgent ? { agentId: nextAgent } : undefined);
      setData(normalizePerception(live));
      if (live.agents?.length) setAgents(live.agents);
      const prefs = loadCalibPrefs();
      if (!prefs && live.calibration) {
        setThreshold(Math.round((live.calibration.concernThreshold || 0.72) * 100));
        setFloor(Math.round((live.calibration.confidenceFloor || 0.65) * 100));
        setPolicy(live.calibration.policy || "Balanced");
        setRequireTwo(live.calibration.requireTwoModalities !== false);
      }
    } catch {
      /* keep last / empty */
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(range, agentId);
  }, [range, agentId]);

  useEffect(() => {
    void api.listAgents().then((r) => {
      const ids = (r.agents || []).map((a) => a.agentId).filter(Boolean);
      if (ids.length) setAgents((prev) => Array.from(new Set([...prev, ...ids])));
    }).catch(() => undefined);
  }, []);

  const clouds = useMemo(() => {
    const raw = data.landscape.clouds || [];
    const pts = data.landscape.points || [];
    if (!pts.length) return raw;
    const buckets = new Map<string, { sx: number; sy: number; n: number }>();
    for (const p of pts) {
      const key = raw.find((c) => c.color === p.color)?.name
        || raw.find((c) => c.name.toLowerCase() === String(p.label || "").toLowerCase())?.name;
      if (!key) continue;
      const v = Number(p.valence);
      const a = Number(p.arousal);
      if (!Number.isFinite(v) || !Number.isFinite(a)) continue;
      const cur = buckets.get(key) || { sx: 0, sy: 0, n: 0 };
      cur.sx += v;
      cur.sy += a;
      cur.n += 1;
      buckets.set(key, cur);
    }
    return raw.map((c) => {
      const b = buckets.get(c.name);
      if (!b || b.n === 0) return { ...c, count: c.count ?? 0 };
      return {
        ...c,
        x: (b.sx / b.n) * 720,
        y: (1 - b.sy / b.n) * 210,
        count: b.n,
      };
    });
  }, [data.landscape.clouds, data.landscape.points]);

  const basePoints = useMemo(() => {
    const pts = data.landscape.points;
    if (pts.length) {
      return pts.map((p, i) => ({
        x: p.x ?? Number(p.valence) * 720,
        y: p.y ?? (1 - Number(p.arousal)) * 210,
        color: p.color || "#1c7dff",
        key: p.key || `p-${i}`,
        source: p.source,
      }));
    }
    return [] as { x: number; y: number; color: string; key: string; source?: string }[];
  }, [data.landscape.points]);

  const visiblePoints = useMemo(() => {
    const filtered = combined
      ? basePoints
      : basePoints.filter((p) => p.source === "voice" || p.source === "language");
    if (chartMode === "Timeline") {
      return filtered.map((p, i) => ({
        ...p,
        x: 20 + (i % 40) * 17,
        y: 190 - ((i * 29) % 165),
      }));
    }
    return filtered;
  }, [basePoints, chartMode, combined]);

  const heat = data.heatmap.colors;
  const s = data.summary;
  const cal = data.calibration;
  const flows = data.behaviorPaths.flows || [];
  const { line: curveLine, fill: curveFill } = useMemo(
    () => curvePaths(cal.curve || []),
    [cal.curve],
  );
  const threshX = threshold * 3;
  const threshY = curveYAt(cal.curve || [], threshold / 100);

  const livePrecision = useMemo(() => {
    // Re-estimate at current threshold from curve mass above threshold
    const curve = cal.curve || [];
    if (!curve.length) return cal.precision;
    const above = curve.filter((p) => p.x >= threshold / 100).reduce((a, p) => a + p.y, 0);
    const total = curve.reduce((a, p) => a + p.y, 0) || 1;
    const flagShare = above / total;
    // Blend agreement precision with threshold selectivity
    return Math.round((cal.precision * 0.65 + (1 - flagShare) * 35) * 10) / 10;
  }, [cal.curve, cal.precision, threshold]);

  const liveRecall = useMemo(() => {
    const curve = cal.curve || [];
    if (!curve.length) return cal.recall;
    const above = curve.filter((p) => p.x >= threshold / 100).reduce((a, p) => a + p.y, 0);
    const high = curve.filter((p) => p.x >= 0.55).reduce((a, p) => a + p.y, 0) || 1;
    return Math.round(Math.min(99, (above / high) * 100) * 10) / 10;
  }, [cal.curve, cal.recall, threshold]);

  function applyPolicy(next: string) {
    setPolicy(next);
    if (next === "Sensitive") {
      setThreshold(55);
      setFloor(50);
      setRequireTwo(false);
    } else if (next === "Strict") {
      setThreshold(85);
      setFloor(75);
      setRequireTwo(true);
    } else {
      setThreshold(72);
      setFloor(65);
      setRequireTwo(true);
    }
  }

  function exportInsights() {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `perception-${range}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function testConfig() {
    setToast({ precision: livePrecision, recall: liveRecall });
    window.setTimeout(() => setToast(null), 2800);
  }

  const maxDriver = Math.max(1, ...data.concernDrivers.map((d) => d.value));

  return <>
    <PageHeader
      title="Perception"
      subtitle="Understand behavioral patterns and tune multimodal signal detection."
      actions={<>
        <Select
          size="sm"
          variant="toolbar"
          searchable={false}
          ariaLabel="Date range"
          value={range}
          onChange={(v) => setRange((v ?? "30d") as typeof range)}
          options={[
            { value: "30d", label: "Last 30 days", icon: <CalendarDays size={14} /> },
            { value: "7d", label: "Last 7 days", icon: <CalendarDays size={14} /> },
            { value: "24h", label: "Last 24 hours", icon: <CalendarDays size={14} /> },
          ]}
        />
        <Select
          size="sm"
          variant="toolbar"
          searchable={false}
          ariaLabel="Agent filter"
          value={agentId || "all"}
          onChange={(v) => setAgentId(v === "all" ? "" : (v ?? ""))}
          options={[
            { value: "all", label: "All agents", icon: <UserRound size={14} /> },
            ...agents.map((id) => ({ value: id, label: id, icon: <UserRound size={14} /> })),
          ]}
        />
        <Select
          size="sm"
          variant="toolbar"
          searchable={false}
          ariaLabel="Language filter"
          value={languageFilter}
          onChange={(v) => setLanguageFilter(v ?? "all")}
          options={[
            { value: "all", label: "All languages", icon: <Globe2 size={14} /> },
            { value: "en", label: "English", icon: <Globe2 size={14} /> },
            { value: "es", label: "Spanish", icon: <Globe2 size={14} /> },
          ]}
        />
        <button className="btn primary" type="button" onClick={exportInsights}><Download />Export insights</button>
      </>}
    />

    <section className="card perception-summary">
      <PStat icon={<Database />} label="Multimodal coverage" value={`${s.multimodalCoveragePct}%`} delta={fmtDelta(s.multimodalCoverageDelta, true)} />
      <PStat icon={<Waves />} label="High-confidence signals" value={fmtInt(s.highConfidenceSignals)} delta={fmtDelta(s.highConfidenceDelta)} />
      <PStat icon={<span style={{ fontSize: 19 }}>☹</span>} label="Concern → recovery" value={`${s.concernRecoveryPct}%`} delta={fmtDelta(s.concernRecoveryDelta, true)} tone="red" />
      <PStat icon={<AlertTriangle />} label="Needs review" value={fmtInt(s.needsReview)} delta={fmtDelta(s.needsReviewDelta)} tone="orange" />
    </section>

    {data.channels ? (
      <section className="card perception-channels">
        <span className="perception-channels-title">Signal channels</span>
        <ChannelStat name="Language" ready={data.channels.language} total={data.channels.sessions} />
        <ChannelStat name="Voice" ready={data.channels.voice} total={data.channels.sessions} />
        <ChannelStat name="Video" ready={data.channels.video} total={data.channels.videoExpected} note="with camera" />
        {data.fusion ? (
          <span className="perception-fusion-dist">
            {FUSION_LABELS.map(([key, label]) => `${label} ${fmtInt(data.fusion?.[key] ?? 0)}`).join(" · ")}
          </span>
        ) : null}
      </section>
    ) : null}

    <div className="perception-layout" style={{ opacity: loading ? 0.72 : 1 }}>
      <div className="perception-main">
        <Card
          className="emotion-card"
          title="Emotion landscape"
          action={
            <div className="toolbar-group">
              <div className="segmented">
                {["Valence × arousal", "Timeline"].map((x) => (
                  <button key={x} className={chartMode === x ? "active" : ""} onClick={() => setChartMode(x)}>{x}</button>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 7, color: "#0968f2", fontSize: 9 }}>
                <Switch checked={combined} onCheckedChange={setCombined} />Combined signals
              </label>
            </div>
          }
        >
          <div className="emotion-chart">
            <span className="axis-label y-top">High energy</span>
            <span className="axis-label y-mid">Arousal</span>
            <span className="axis-label y-bottom">Low energy</span>
            <span className="axis-label x-left">Negative</span>
            <span className="axis-label x-mid">Valence</span>
            <span className="axis-label x-right">Positive</span>
            <svg className="emotion-svg" viewBox="0 0 720 210" preserveAspectRatio="xMidYMid meet">
              <defs>
                <pattern id="grid" width="48" height="34" patternUnits="userSpaceOnUse">
                  <path d="M48 0H0V34" fill="none" stroke="#e9eef4" strokeWidth="1" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="720" height="210" fill="url(#grid)" />
              <path d="M0 205H720M0 0V205" stroke="#8091a5" strokeWidth="1" />
              {visiblePoints.map((p) => (
                <circle key={p.key} cx={p.x} cy={p.y} r={combined ? 1.25 : 0.75} fill={p.color} opacity={combined ? 0.46 : 0.25} />
              ))}
              {clouds.filter((c) => (c.count ?? 0) > 0 || visiblePoints.length === 0).map((c) => (
                <circle key={c.name} cx={c.x} cy={c.y} r="5" fill={c.color} stroke="#fff" strokeWidth="2" />
              ))}
            </svg>
            {chartMode !== "Timeline" && clouds.filter((c) => (c.count ?? 0) > 0 || visiblePoints.length === 0).map((c) => (
              <span key={c.name} className="cluster-label" style={{ color: c.color, left: `${(c.x / 720) * 88 + 7}%`, top: `${(c.y / 210) * 78}%` }}>{c.name}</span>
            ))}
          </div>
          <div className="emotion-legend">
            <span><i style={{ background: "#0968f2" }} />Voice</span>
            <span><i style={{ background: "#8646f5" }} />Face</span>
            <span><i style={{ background: "#ed8b0d" }} />Language</span>
            <span><i style={{ background: "#0aa878" }} />Body</span>
          </div>
        </Card>

        <div className="perception-lower">
          <Card title="Signal intensity by journey stage">
            <div className="heatmap">
              <span />
              {STAGE_LABELS.map((x) => <span className="hm-head" key={x}>{x}</span>)}
              {HEAT_LABELS.map((label, r) => (
                <div key={label} style={{ display: "contents" }}>
                  <span className="hm-label">{label}</span>
                  {(heat[r] || []).map((c, i) => {
                    const v = data.heatmap.values?.[r]?.[i];
                    const valueText = v != null ? `${Math.round(v * 100)}%` : "No data";
                    return (
                      <button
                        aria-label={`${label}, ${STAGE_LABELS[i] || `stage ${i + 1}`}: ${valueText}`}
                        title={`${label} · ${STAGE_LABELS[i] || `Stage ${i + 1}`}: ${valueText}`}
                        key={i}
                        className="hm-cell"
                        style={{ background: c, border: 0 }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="heatmap-legend">
              <span>Low</span>
              {["#d8e8fb", "#bdd7f9", "#9dbcf3", "#b48cf5", "#8a48ef"].map((x) => <i key={x} style={{ background: x }} />)}
              <span>High</span>
            </div>
          </Card>

          <Card title="Behavior paths">
            {flows.length === 0 && data.behaviorPaths.triggers.length === 0 ? (
              <div className="empty-state" style={{ padding: "28px 12px", fontSize: 11, color: "#687b91" }}>
                No path triples yet — need sessions with language or voice signals.
              </div>
            ) : (
              <>
                <div className="behavior-columns">
                  <svg className="behavior-flow" viewBox="0 0 260 160" preserveAspectRatio="none">
                    {flows.map((f) => (
                      <path
                        key={`${f.trigger}-${f.state}-${f.outcome}-${f.triggerIdx}`}
                        d={flowSvgPath(f.triggerIdx)}
                        fill="none"
                        stroke={f.color}
                        strokeWidth={f.weight}
                        opacity={0.85}
                      />
                    ))}
                  </svg>
                  <BCol title="Trigger" nodes={data.behaviorPaths.triggers} />
                  <BCol title="State" nodes={data.behaviorPaths.states} />
                  <BCol title="Outcome" nodes={data.behaviorPaths.outcomes} />
                </div>
                <div className="behavior-note">
                  <Waves size={15} />
                  <span>
                    <b>{data.behaviorPaths.noteTitle}</b>
                    <br />
                    {data.behaviorPaths.noteBody}
                  </span>
                </div>
              </>
            )}
          </Card>
        </div>

        <div className="insight-banner">
          <Sparkles size={20} />
          <span>
            <b>Perception insight</b>
            <span>{data.insightBanner || data.insight || "Collect more sessions to surface insights."}</span>
          </span>
          <a href="/sessions">Explore correlation →</a>
        </div>
      </div>

      <div className="stack">
        <Card
          title="What drives concern"
          action={
            <span className="delta">
              {data.concernDeltaPct != null ? fmtDelta(data.concernDeltaPct, true) : "—"}{" "}
              <span className="muted">vs previous period</span>
            </span>
          }
        >
          {data.concernDrivers.length === 0 ? (
            <div style={{ padding: "18px 4px", fontSize: 11, color: "#687b91" }}>
              No concern turns in this range yet.
            </div>
          ) : (
            data.concernDrivers.map((row) => (
              <div key={row.label} className="concern-row">
                <span>{row.label}</span>
                <span className="concern-bar">
                  <i style={{ width: `${Math.min(100, (row.value / maxDriver) * 100)}%`, background: row.color }} />
                </span>
                <b>{row.value}%</b>
              </div>
            ))
          )}
          <div className="insight-box">
            <Sparkles size={22} />
            <span>
              <b>Insight</b>
              <span>{data.insight || "—"}</span>
            </span>
          </div>
        </Card>

        <Card title="Detection calibration">
          <div className="calibration-scores">
            <div className="calibration-score">
              <span>◎ &nbsp; Precision</span>
              <b>{livePrecision}%</b>
            </div>
            <div className="calibration-score">
              <span>▥ &nbsp; Recall</span>
              <b>{liveRecall}%</b>
            </div>
          </div>
          {cal.metricNote && (
            <div style={{ color: "#8b98a8", fontSize: 7, margin: "4px 0 8px", lineHeight: 1.35 }}>
              {cal.metricNote}
              {cal.scoreCount != null ? ` · ${cal.scoreCount} scores` : ""}
            </div>
          )}
          <div className="slider-row">
            <span>Concern threshold</span>
            <input
              aria-label="Concern threshold"
              className="range"
              type="range"
              min="0"
              max="100"
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
            <b>{(threshold / 100).toFixed(2)}</b>
          </div>
          <div className="slider-row">
            <span>Confidence floor</span>
            <input
              aria-label="Confidence floor"
              className="range"
              style={{ accentColor: "#8057e8" }}
              type="range"
              min="0"
              max="100"
              value={floor}
              onChange={(e) => setFloor(Number(e.target.value))}
            />
            <b>{(floor / 100).toFixed(2)}</b>
          </div>
          <div className="switch-row" style={{ padding: "4px 0 10px" }}>
            <span className="switch-copy"><b>Require 2 modalities</b></span>
            <Switch checked={requireTwo} onCheckedChange={setRequireTwo} />
          </div>
          <div className="segmented" style={{ width: "100%" }}>
            {["Balanced", "Sensitive", "Strict"].map((x) => (
              <button key={x} style={{ flex: 1 }} className={policy === x ? "active" : ""} onClick={() => applyPolicy(x)}>{x}</button>
            ))}
          </div>
          <div className="calibration-curve">
            <svg viewBox="0 0 300 70" preserveAspectRatio="none">
              <defs>
                <linearGradient id="curveFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#1675ff" stopOpacity=".25" />
                  <stop offset="1" stopColor="#8057e8" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="curveStroke">
                  <stop stopColor="#0ab38a" />
                  <stop offset=".52" stopColor="#0968f2" />
                  <stop offset="1" stopColor="#8057e8" />
                </linearGradient>
              </defs>
              <path d={curveFill} fill="url(#curveFill)" />
              <path d={curveLine} fill="none" stroke="url(#curveStroke)" strokeWidth="2" />
              <line x1={threshX} y1="0" x2={threshX} y2="70" stroke="#0968f2" strokeDasharray="4 3" />
              <circle cx={threshX} cy={threshY} r="3" fill="#0968f2" />
            </svg>
            <div className="calibration-axis">
              <span>0.0</span><span>0.2</span><span>0.4</span><span>0.6</span><span>0.8</span><span>1.0</span>
            </div>
          </div>
          <button className="btn primary" style={{ width: "100%", marginTop: 10 }} onClick={testConfig}>
            <Play />Test configuration
          </button>
          <a href="/sessions" style={{ marginTop: 10, display: "block", color: "#0968f2", fontSize: 8, textAlign: "center" }}>
            Open review queue · {cal.reviewQueueCount} →
          </a>
        </Card>
      </div>
    </div>

    {toast && (
      <div className="toast">
        <span className="toast-icon">✓</span>
        <span>
          <b>Calibration test complete</b>
          <span>Precision {toast.precision}% · Recall {toast.recall}% at threshold {(threshold / 100).toFixed(2)}.</span>
        </span>
      </div>
    )}
  </>;
}

function ChannelStat({ name, ready, total, note }: { name: string; ready: number; total: number; note?: string }) {
  const pct = total ? Math.round((ready / total) * 100) : 0;
  return (
    <div className="perception-channel-stat">
      <span>
        <b>{name}</b> {fmtInt(ready)} / {fmtInt(total)}{note ? ` ${note}` : ""}
      </span>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PStat({ icon, label, value, delta, tone = "blue" }: { icon: React.ReactNode; label: string; value: string; delta: string; tone?: string }) {
  const style = tone === "red"
    ? { background: "#fff0f1", color: "#e14d5a" }
    : tone === "orange"
      ? { background: "#fff2e7", color: "#ef7918" }
      : undefined;
  return (
    <div className="perception-stat">
      <span className="perception-stat-icon" style={style}>{icon}</span>
      <span>
        <span className="perception-stat-title">{label}</span>
        <b>{value}</b>
        <span className="perception-delta">{delta}</span>
        <span className="perception-stat-foot">vs. previous period</span>
      </span>
    </div>
  );
}

function BCol({ title, nodes }: { title: string; nodes: string[][] }) {
  return (
    <div className="behavior-col">
      <span>{title}</span>
      {nodes.map(([name, color], i) => (
        <div
          className="behavior-node"
          key={`${title}-${i}-${name}`}
          style={name === "—" ? { opacity: 0.35 } : undefined}
        >
          <i style={{ background: color }} />{name}
        </div>
      ))}
    </div>
  );
}
